import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Row, Col, Card, Table, Button, Form,
  InputGroup, Spinner, OverlayTrigger, Tooltip, Pagination, Modal
} from 'react-bootstrap';
import { getApiUrl } from '../config/api';

const styles = {
  appContainer: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 40%, #e2e8f0 100%)',
    display: 'flex', flexDirection: 'column',
  },
  navbarCustom: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0d9488 100%)',
    padding: '0.25rem 1rem', flexShrink: 0,
  },
  mainContent: {
    flex: 1, padding: '0.4rem', overflowY: 'auto',
  },
  mainCard: {
    borderRadius: '12px', border: '1.5px solid #cbd5e1',
    boxShadow: '0 8px 30px rgba(15, 23, 42, 0.06)',
    background: '#ffffff', overflow: 'hidden',
  },
  cardHeader: {
    padding: '0.4rem 0.75rem', color: '#ffffff', fontWeight: 600, fontSize: '0.83rem',
  },
  bgMenuHeader: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0d9488 100%)',
  },
};

const SignaObat = () => {
  const navigate = useNavigate();
  const [time, setTime] = useState({ date: '', clock: '' });

  // ── Print Preview State ──
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printUrl, setPrintUrl] = useState('');
  const [printTitle, setPrintTitle] = useState('');
  const iframeRef = React.useRef(null);

  // ── State Management ──
  const [nomorNota, setNomorNota] = useState('');
  const [signaList, setSignaList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);

  // Default / Mock Data from User Request
  const mockData = [
    { dosis: "2 X SEHARI 1", kd_obat: "00012674", nm_obat: "Captopril 25 mg", no_faktur: "A01012026176", qty: "21", satuan: "Tablet", tgl_exp: "28-01-2029" },
    { dosis: "3 XSEHARI 1", kd_obat: "00012693", nm_obat: "Acyclovir 400 mg", no_faktur: "A01012026176", qty: "20", satuan: "Tablet", tgl_exp: "28-04-2027" },
    { dosis: "2 X SEHARI 1", kd_obat: "00024384", nm_obat: "Cetirizine 10 mg", no_faktur: "A01012026176", qty: "10", satuan: "Tablet", tgl_exp: "28-07-2027" },
    { dosis: "2-3 X SEHARI OLES TIPIS", kd_obat: "00025002", nm_obat: "Acyclovir 5 % Cream 5 Gram", no_faktur: "A01012026176", qty: "1", satuan: "Tube", tgl_exp: "28-06-2027" },
    { dosis: "3 XSEHARI 1", kd_obat: "00025014", nm_obat: "Paracetamol 500 mg", no_faktur: "A01012026176", qty: "10", satuan: "Tablet", tgl_exp: "28-07-2027" }
  ];

  // ── Fetch Data ──
  const fetchDosis = async (nota = nomorNota) => {
    if (!nota) return;
    setIsLoading(true);
    setFetchError('');
    const apiUrl = getApiUrl();
    
    try {
      const res = await fetch(`${apiUrl}/show_dosis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nonota: nota })
      });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const json = await res.json();
      setSignaList(json?.list_signa || mockData);
    } catch (err) {
      console.warn('Gagal fetch /show_dosis:', err);
      setSignaList(mockData);
      setFetchError('Menggunakan data lokal. Gagal terhubung ke server backend.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchDosis();
  };

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setTime({
        date: now.toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
        clock: now.toLocaleTimeString('id-ID') + ' WIB',
      });
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // ── Pagination Logic ──
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = signaList.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(signaList.length / itemsPerPage);

  const handleProses = () => {
    fetchDosis();
  };

  const handlePrintRow = (item) => {
    const apiUrl = getApiUrl();
    const url = `${apiUrl}/printetiket/${item.kd_obat}/${encodeURIComponent(item.nm_obat)}/${encodeURIComponent(item.satuan)}/${item.qty}/${encodeURIComponent(item.dosis)}/${item.tgl_exp}/${item.no_faktur}`;
    setPrintUrl(url);
    setPrintTitle(`E-Tiket: ${item.nm_obat}`);
    setShowPrintModal(true);
  };

  const executePrint = () => {
    try {
      if (iframeRef.current) {
        iframeRef.current.contentWindow.focus();
        iframeRef.current.contentWindow.print();
      }
    } catch (err) {
      console.warn("Cross-origin print blocked. Fallback to window.open", err);
      window.open(printUrl, '_blank');
    }
  };

  const handleEditRow = (item) => {
    alert(`Edit dosis obat: ${item.nm_obat}`);
  };

  return (
    <div style={styles.appContainer}>
      
      {/* ══ NAVBAR ══ */}
      <nav className="navbar navbar-expand-xl navbar-dark shadow-sm" style={styles.navbarCustom}>
        <Container fluid>
          <a className="navbar-brand d-flex align-items-center me-3" href="#home"
            onClick={e => { e.preventDefault(); navigate('/home'); }}>
            <div className="d-flex align-items-center justify-content-center me-2 rounded bg-white bg-opacity-10 p-1">
              <i className="bi bi-shield-plus text-warning fs-4"></i>
            </div>
            <div>
              <div className="fw-bold text-white lh-1" style={{ fontSize: '0.95rem' }}>
                SIMRS2 <span style={{ fontSize: '0.78rem', color: '#2dd4bf' }}>Farmasi</span>
              </div>
              <div className="text-white-50" style={{ fontSize: '0.62rem' }}>Sistem Informasi Rumah Sakit</div>
            </div>
          </a>
          <div className="d-flex align-items-center gap-3 ms-auto">
            <div className="small border-end pe-3 border-light border-opacity-25 d-none d-sm-block">
              <div className="fw-semibold text-white-50" style={{ fontSize: '0.73rem' }}>{time.date}</div>
              <div className="fw-bold" style={{ fontSize: '0.78rem', color: '#2dd4bf' }}>{time.clock}</div>
            </div>
            <Button variant="light" size="sm" onClick={() => navigate('/home')} className="d-flex align-items-center gap-1">
              <i className="bi bi-house-door-fill" style={{ color: '#0d9488' }}></i>
              <span>Kembali</span>
            </Button>
          </div>
        </Container>
      </nav>

      {/* ══ MAIN CONTENT ══ */}
      <div style={styles.mainContent}>
        <Card style={styles.mainCard} className="shadow-sm border-0">
          <div style={{ ...styles.cardHeader, ...styles.bgMenuHeader }} className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span className="fw-bold d-flex align-items-center gap-2">
              <i className="bi bi-list-ul text-warning fs-6"></i>
              DOSIS OBAT PASIEN
            </span>
            <div className="d-flex gap-2">
              <Button variant="primary" size="sm" onClick={handleProses} className="d-flex align-items-center gap-1 fw-semibold px-2" style={{ background: '#3b82f6', borderColor: '#3b82f6', fontSize: '0.75rem' }}>
                <i className="bi bi-arrow-clockwise"></i> Proses
              </Button>
            </div>
          </div>
          <Card.Body className="p-3">
            
            {/* ── TOOLBAR: Search & Items Per Page ── */}
            <Row className="g-2 mb-3 align-items-end justify-content-between">
              <Col md={6} lg={4}>
                <Form onSubmit={handleSearchSubmit}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                      Nomor Nota
                    </Form.Label>
                    <InputGroup size="sm">
                      <Form.Control
                        type="text"
                        placeholder="Contoh: 00001/01/1/2026/000001"
                        value={nomorNota}
                        onChange={(e) => setNomorNota(e.target.value)}
                        style={{ fontSize: '0.8rem', color: '#1e293b' }}
                      />
                      <Button type="submit" variant="light" className="border d-flex align-items-center justify-content-center" style={{ width: '40px' }}>
                        {isLoading ? <Spinner size="sm" animation="border" /> : <i className="bi bi-search text-muted"></i>}
                      </Button>
                    </InputGroup>
                  </Form.Group>
                </Form>
              </Col>

              <Col md={4} lg={3} className="d-flex justify-content-end align-items-center gap-2">
                <span className="text-muted" style={{ fontSize: '0.75rem' }}>Tampilkan</span>
                <Form.Select 
                  size="sm" 
                  value={itemsPerPage} 
                  onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  style={{ width: '80px', fontSize: '0.75rem' }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Form.Select>
                <span className="text-muted" style={{ fontSize: '0.75rem' }}>data / halaman</span>
              </Col>
            </Row>

            {fetchError && (
              <div className="alert alert-warning py-1 px-3 mb-3 d-flex align-items-center gap-2" style={{ fontSize: '0.75rem' }}>
                <i className="bi bi-exclamation-triangle-fill text-warning"></i>
                {fetchError}
              </div>
            )}

            {/* ── TABEL DATA ── */}
            <div className="table-responsive border rounded" style={{ minHeight: '300px' }}>
              <Table responsive hover striped bordered size="sm" className="mb-0 align-middle" style={{ fontSize: '0.75rem' }}>
                <thead style={{ background: '#94a3b8', color: '#1e293b', borderBottom: '2px solid #cbd5e1' }}>
                  <tr>
                    <th className="text-center py-2" style={{ width: '100px' }}>Kode</th>
                    <th className="py-2">Nama Obat</th>
                    <th className="text-center py-2" style={{ width: '60px' }}>Qty</th>
                    <th className="text-center py-2" style={{ width: '80px' }}>Satuan</th>
                    <th className="py-2">Dosis</th>
                    <th className="text-center py-2" style={{ width: '100px' }}>Expired Date</th>
                    <th className="text-center py-2" style={{ width: '130px' }}>Faktur</th>
                    <th className="text-center py-2" style={{ width: '80px' }}>#</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="8" className="text-center py-5 text-muted">
                        <Spinner animation="border" variant="secondary" size="sm" className="mb-2" />
                        <div>Memuat data dosis obat...</div>
                      </td>
                    </tr>
                  ) : currentItems.length > 0 ? (
                    currentItems.map((item, idx) => (
                      <tr key={idx}>
                        <td className="text-center font-monospace text-primary fw-semibold">{item.kd_obat}</td>
                        <td className="fw-semibold text-dark">{item.nm_obat}</td>
                        <td className="text-center">{item.qty}</td>
                        <td className="text-center text-muted">{item.satuan}</td>
                        <td className="text-danger fw-semibold">{item.dosis}</td>
                        <td className="text-center font-monospace text-muted">{item.tgl_exp}</td>
                        <td className="text-center font-monospace text-muted">{item.no_faktur}</td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            <OverlayTrigger overlay={<Tooltip>Cetak Dosis</Tooltip>}>
                              <span style={{ cursor: 'pointer', color: '#0ea5e9' }} onClick={() => handlePrintRow(item)}>
                                <i className="bi bi-printer-fill fs-6"></i>
                              </span>
                            </OverlayTrigger>
                            <OverlayTrigger overlay={<Tooltip>Edit Dosis</Tooltip>}>
                              <span style={{ cursor: 'pointer', color: '#64748b' }} onClick={() => handleEditRow(item)}>
                                <i className="bi bi-pencil-square fs-6"></i>
                              </span>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center py-4 text-muted">
                        Data belum tersedia. Silakan cari Nomor Nota.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* ── PAGINATION & FOOTER ── */}
            <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top flex-wrap gap-2">
              <span className="text-muted fw-semibold fst-italic" style={{ fontSize: '0.75rem' }}>
                Data {signaList.length > 0 ? indexOfFirst + 1 : 0} sampai {Math.min(indexOfLast, signaList.length)} dari {signaList.length} Data
              </span>
              
              {totalPages > 1 && (
                <Pagination size="sm" className="mb-0">
                  <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                  {[...Array(Math.min(totalPages, 5))].map((_, i) => (
                    <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
                      {i + 1}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                </Pagination>
              )}
            </div>

          </Card.Body>
        </Card>
      </div>

      {/* ── PRINT PREVIEW MODAL ── */}
      <Modal show={showPrintModal} onHide={() => setShowPrintModal(false)} size="lg" centered backdrop="static">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title style={{ fontSize: '1rem' }}>
            <i className="bi bi-printer-fill me-2 text-primary"></i>
            Cetak Preview - {printTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ height: '70vh', background: '#525659' }}>
          {showPrintModal && printUrl && (
            <iframe
              ref={iframeRef}
              src={printUrl}
              title="Print Preview"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="secondary" size="sm" onClick={() => setShowPrintModal(false)} className="fw-semibold px-3">
            Batal
          </Button>
          <Button variant="primary" size="sm" onClick={executePrint} className="d-flex align-items-center gap-1 fw-bold px-4" style={{ background: '#3b82f6' }}>
            <i className="bi bi-printer"></i> Cetak Sekarang
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default SignaObat;
